import { useEffect, useCallback } from 'react'

interface UseKeyboardShortcutProps {
  key: string
  metaKey?: boolean
  ctrlKey?: boolean
  shiftKey?: boolean
  altKey?: boolean
  callback: () => void
  dependencies?: any[]
}

export function useKeyboardShortcut({
  key,
  metaKey = false,
  ctrlKey = false,
  shiftKey = false,
  altKey = false,
  callback,
  dependencies = [],
}: UseKeyboardShortcutProps) {
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null
      const isTargetInput =
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target?.isContentEditable

      // Don't trigger shortcut if user is typing in an input field
      // unless it's a modal or special case
      if (isTargetInput && !target?.closest('[data-command-palette]')) {
        return
      }

      const keyMatches = event.key.toLowerCase() === key.toLowerCase()
      const metaMatches = event.metaKey === metaKey
      const ctrlMatches = event.ctrlKey === ctrlKey
      const shiftMatches = event.shiftKey === shiftKey
      const altMatches = event.altKey === altKey

      if (keyMatches && metaMatches && ctrlMatches && shiftMatches && altMatches) {
        event.preventDefault()
        event.stopPropagation()
        callback()
      }
    },
    [key, metaKey, ctrlKey, shiftKey, altKey, callback, ...dependencies]
  )

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [handleKeyDown])
}

// Convenience hook for Cmd+K (or Ctrl+K on Windows/Linux)
export function useCommandPalette(callback: () => void, dependencies: any[] = []) {
  const isMac =
    typeof navigator !== 'undefined' && navigator.platform.toUpperCase().indexOf('MAC') >= 0

  useKeyboardShortcut({
    key: 'k',
    metaKey: isMac,
    ctrlKey: !isMac,
    callback,
    dependencies,
  })
}
